import {fromNullable, none, Option, some} from "fp-ts/lib/Option"
import {throttle} from "lodash"
import {AddBookingState} from "./components/AddBookingWizard"

// Simple gettable/settable thing
interface Box<T> {
  // TypeScript is a POS and doesn't enforce that mutable properties are actually mutable (i.e.
  // are access descriptors with a setter, or are data descriptors with writable=true). It also
  // treats mutable properties as covariant instead of invariant. :'(
  value: T
}

function maybeLocalStorage(): Option<Storage> {
  // As far as I can tell this is the best way to test if the browser supports local storage
  return localStorage && Storage && localStorage instanceof Storage ? some(localStorage) : none
}

function createLocalStorageBox(key: string): Box<Option<string>> {
  return maybeLocalStorage().foldL(() => new NoneBox(), (x) => new LocalStorageBox(x, key))
}

class LocalStorageBox implements Box<Option<string>> {
  constructor(private storage: Storage, private key: string) {}

  get value() {
    return fromNullable(this.storage.getItem(this.key))
  }

  set value(x: Option<string>) {
    if (x.isSome()) {
      this.storage.setItem(this.key, x.value)
    } else {
      this.storage.removeItem(this.key)
    }
  }
}

// I don't know how to make this statically assert that T can be
// converted to JSON and back again. I tried "T extends JSONValue" with JSONValue
// from here:
// https://github.com/Microsoft/TypeScript/issues/3496#issuecomment-128553540
// but then it requires T to be something with an index signature. :(
class JsonBox<T> implements Box<Option<T>> {
  constructor(private inner: Box<Option<string>>) {}

  get value() {
    return this.inner.value.chain((x) => fromNullable(JSON.parse(x)))
  }

  set value(x: Option<T>) {
    this.inner.value = some(JSON.stringify(x.toNullable()))
  }
}

/**
 * Dumb box that ignores sets and spits out nones
 */
class NoneBox<T> implements Box<Option<T>> {
  get value() {
    return none
  }

  set value({}: Option<T>) {
    // pass
  }
}

interface Stamped<T> {
  stamp: number
  booking: T
}

const maxAgeMillis: number = 30 * 60 * 1000

function nowMillis(): number {
  return Date.now()
}

class StampedBox<T> implements Box<Option<T>> {
  constructor(private inner: Box<Option<Stamped<T>>>) {}

  get value() {
    return this.inner.value
      .filter((x) => {
        const age = nowMillis() - x.stamp
        return age < maxAgeMillis
      })
      .map((x) => x.booking)
  }

  set value(x: Option<T>) {
    this.inner.value = x.map((x) => ({
      stamp: nowMillis(),
      booking: x,
    }))
  }
}

class WriteThroughCachingBox<T> implements Box<T> {
  private cache: Option<T> = none

  constructor(private inner: Box<T>) {}

  get value() {
    return this.cache.getOrElseL(this.fetchNew)
  }

  set value(x: T) {
    this.cache = some(x)
    this.inner.value = x
  }

  fetchNew = () => {
    const {value} = this.inner
    this.cache = some(value)
    return value
  }
}

class ThrottledWriteBox<T> implements Box<T> {
  constructor(private inner: Box<T>) {}

  get value() {
    return this.inner.value
  }

  set value(x: T) {
    this.doWrite(x)
  }

  private doWrite = throttle(
    (x: T) => {
      this.inner.value = x
    },
    1000,
    {leading: false},
  )
}

// - WriteThroughCachingBox so that getting the persisted state is cheap enough to do in a
// React render()
// - ThrottledWriteBox so we don't convert to JSON and write to local storage on every single
// state change
// - StampedBox handles the stamp
// - JsonBox converts to/from JSON
// - createLocalStorageBox read/writes a key in local storage
export const persistedState: Box<Option<AddBookingState>> = new WriteThroughCachingBox(
  new ThrottledWriteBox(new StampedBox(new JsonBox(createLocalStorageBox("state")))),
)



// WEBPACK FOOTER //
// ./src/localStorage.ts